import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faFile,
  faFileLines,
  faTableCells,
  faFileArrowUp,
  faFolderPlus,
  faDownload,
  faArrowLeft,
  faHardDrive,
  faBuilding,
  faUsers,
  faPaste,
  faCopy,
  faScissors,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { openWindow } from "../../Slices/windowsSlice";
import { setClipboard, clearClipboard } from "../webtopUiSlice";
import ContextMenu from "../desktop/ContextMenu";
import {
  listFiles,
  getStorage,
  createFolder,
  updateNode,
  deleteNode,
  uploadAndCreate,
  pasteNode,
  fileDownloadUrl,
  isImage,
  isText,
  isSheet,
  createSheetFile,
} from "./filesApi";
import { emptySheetContent } from "../sheets/sheetModel";

const glyphForNode = (node) => {
  if (node.type === "folder") return faFolder;
  if (isSheet(node)) return faTableCells;
  if (node.type === "textfile" || isText(node)) return faFileLines;
  return faFile;
};

const GB = 1024 * 1024 * 1024;
// Per-drive capacity (so "This PC" shows a Windows-style usage bar).
const QUOTAS = { personal: 2 * GB, organization: 20 * GB, department: 10 * GB };
const fmtGB = (bytes) => `${(Math.max(0, bytes) / GB).toFixed(2)} GB`;

// Pixels the cursor must travel after mousedown before we treat it as a
// rubber-band drag-select (so a normal click doesn't start a marquee).
const MARQUEE_THRESHOLD = 5;

// Normalize the Redux clipboard to an array of nodes. Tolerates BOTH the new
// multi-select shape ({ mode, nodes:[...] }) AND the legacy single-item shape
// ({ id, mode, scope, name, type }) still written by DesktopFiles / stale state.
const clipboardNodes = (clip) => {
  if (!clip) return [];
  if (Array.isArray(clip.nodes)) return clip.nodes;
  return clip.id ? [{ id: clip.id, scope: clip.scope, name: clip.name, type: clip.type }] : [];
};

/**
 * FileExplorerApp — the "This PC" / Files window (Windows Explorer style).
 *
 * "This PC" shows three drives: Personal (the user's own), Organization (shared
 * across the org) and Department (shared across the user's department). Inside a
 * drive you browse folders and files, create folders, upload files (to S3),
 * preview images, download files, rename and delete. Everything is stored in the
 * dashboard per the drive's scope — nothing on the local disk.
 *
 * Selection is Windows-like: click selects, Ctrl/Cmd-click toggles, Shift-click
 * range-selects, and left-dragging empty space draws a rubber-band marquee.
 * Copy/Cut/Delete act on the whole selection. Keyboard: Ctrl+A, Ctrl+C/X/V,
 * Delete, Escape, F2.
 */
const ROOT = { id: null, name: "This PC", scope: null };
const DOUBLE_TAP_MS = 400;

const FileExplorerApp = () => {
  const dispatch = useDispatch();
  const me = useSelector((s) => s?.loginUserInfo?.loginUserDetails) || {};
  const clipboard = useSelector((s) => s?.webtopUi?.clipboard);
  const personalName =
    [me.firstname, me.lastname].filter(Boolean).join(" ").trim() || "My Files";
  const deptName = me.department || "Department";

  const DRIVES = [
    { scope: "personal", name: personalName, sub: "Personal", icon: faHardDrive },
    { scope: "organization", name: "Organization", sub: "Shared with everyone", icon: faBuilding },
    { scope: "department", name: deptName, sub: "Department", icon: faUsers },
  ];

  // Deep-link support: opening a file/folder from the desktop opens This PC
  // navigated to that folder (and previews the file). ?scope&parentId&name&preview
  const [params] = useSearchParams();
  const [path, setPath] = useState(() => {
    const sc = params.get("scope");
    if (!sc) return [ROOT];
    return [ROOT, { scope: sc, id: params.get("parentId") || null, name: params.get("name") || "Folder" }];
  });
  const [pendingPreview, setPendingPreview] = useState(() => params.get("preview") || null);
  const current = path[path.length - 1];
  const scope = current.scope; // null at "This PC"

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [preview, setPreview] = useState(null); // { node, kind: 'image'|'text', text? } | null
  const [storage, setStorage] = useState({}); // { scope: usedBytes }
  const [selected, setSelected] = useState(() => new Set()); // selected node ids
  const [marquee, setMarquee] = useState(null); // { left, top, width, height } viewport px | null

  const fileInputRef = useRef(null);
  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const lastTapRef = useRef({ id: null, time: 0 }); // manual double-click detection
  const selectionAnchorRef = useRef(null); // index for Shift-click range select

  // ── selection helpers ───────────────────────────────────────────────────────
  const clearSelection = () => {
    setSelected(new Set());
    selectionAnchorRef.current = null;
  };
  const selectedNodes = () => items.filter((n) => selected.has(n.id));

  // Native onDoubleClick is unreliable in this Electron app — detect it manually.
  // Plain click selects (and double opens); Ctrl/Cmd toggles; Shift range-selects.
  const handleItemClick = (e, node, index) => {
    if (e.ctrlKey || e.metaKey) {
      setSelected((prev) => {
        const s = new Set(prev);
        if (s.has(node.id)) s.delete(node.id);
        else s.add(node.id);
        return s;
      });
      selectionAnchorRef.current = index;
      lastTapRef.current = { id: null, time: 0 };
      return;
    }
    if (e.shiftKey) {
      const anchor = selectionAnchorRef.current == null ? index : selectionAnchorRef.current;
      const [a, b] = anchor <= index ? [anchor, index] : [index, anchor];
      setSelected(new Set(items.slice(a, b + 1).map((n) => n.id)));
      lastTapRef.current = { id: null, time: 0 };
      return;
    }
    // Plain click: detect a double-tap to open, otherwise select just this item.
    const now = Date.now();
    const last = lastTapRef.current;
    if (last.id === node.id && now - last.time < DOUBLE_TAP_MS) {
      lastTapRef.current = { id: null, time: 0 };
      openItem(node);
      return;
    }
    lastTapRef.current = { id: node.id, time: now };
    setSelected(new Set([node.id]));
    selectionAnchorRef.current = index;
  };

  const load = useCallback(async () => {
    setSelected(new Set());
    selectionAnchorRef.current = null;
    if (!scope) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setItems(await listFiles(scope, current.id));
    } catch (e) {
      setError(e?.message || "Failed to load files");
    } finally {
      setLoading(false);
    }
  }, [scope, current.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-open a deep-linked file (e.g. opened from the desktop) once loaded.
  useEffect(() => {
    if (!pendingPreview || !items.length) return;
    const node = items.find((n) => n.id === pendingPreview);
    if (node) {
      setPendingPreview(null);
      openItem(node);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingPreview]);

  // Refresh drive usage whenever we're back at "This PC".
  useEffect(() => {
    if (scope) return;
    let alive = true;
    getStorage()
      .then((s) => alive && setStorage(s))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [scope]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  // ── navigation ────────────────────────────────────────────────────────────
  const openDrive = (drive) => {
    setPreview(null);
    setPath([ROOT, { id: null, name: drive.name, scope: drive.scope }]);
  };
  const openFolder = (node) => {
    setPreview(null);
    setPath((p) => [...p, { id: node.id, name: node.name, scope }]);
  };
  const goToCrumb = (i) => {
    setPreview(null);
    setPath((p) => p.slice(0, i + 1));
  };
  const back = () => {
    if (preview) return setPreview(null);
    if (path.length > 1) goToCrumb(path.length - 2);
  };

  const openItem = (node) => {
    if (node.type === "folder") return openFolder(node);
    // Spreadsheets (.sheet) open in the Sheets app. Checked before isText because
    // a .sheet is stored as a textfile (which would otherwise open in Notepad).
    if (isSheet(node)) {
      dispatch(openWindow({ path: `/webtop-sheets?fileId=${node.id}`, title: node.name, deduplicate: true }));
      return;
    }
    // Text files open in Notepad (Windows behaviour).
    if (isText(node)) {
      dispatch(openWindow({ path: `/webtop-notepad?fileId=${node.id}`, title: node.name, deduplicate: true }));
      return;
    }
    if (isImage(node)) return setPreview({ node, kind: "image" });
    window.open(fileDownloadUrl(node, "attachment"), "_blank", "noopener");
  };

  // ── actions ───────────────────────────────────────────────────────────────
  const newFolder = () =>
    run(async () => {
      const n = await createFolder("New folder", scope, current.id);
      setItems(await listFiles(scope, current.id));
      if (n?.id) {
        setRenamingId(n.id);
        setRenameValue(n.name);
      }
    });

  const newSheet = () =>
    run(async () => {
      const n = await createSheetFile("Spreadsheet", scope, current.id, emptySheetContent());
      setItems(await listFiles(scope, current.id));
      if (n?.id) {
        dispatch(openWindow({ path: `/webtop-sheets?fileId=${n.id}`, title: n.name, deduplicate: true }));
      }
    });

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    run(async () => {
      for (const f of files) await uploadAndCreate(f, scope, current.id);
    });
  };

  const commitRename = (id) =>
    run(async () => {
      const name = renameValue.trim();
      setRenamingId(null);
      if (name) await updateNode({ id, name });
    });

  const startRename = (node) => {
    setCtx(null);
    setRenamingId(node.id);
    setRenameValue(node.name);
  };

  // Delete one-or-many → Recycle Bin. `nodes` defaults to the current selection.
  const removeNodes = (nodes) => {
    setCtx(null);
    if (!nodes || !nodes.length) return;
    const label =
      nodes.length === 1
        ? nodes[0].type === "folder"
          ? "this folder and everything in it"
          : `"${nodes[0].name}"`
        : `${nodes.length} items`;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete ${label}? It moves to the Recycle Bin.`)) return;
    const ids = new Set(nodes.map((n) => n.id));
    run(async () => {
      for (const n of nodes) await deleteNode(n.id);
      if (preview?.node?.id && ids.has(preview.node.id)) setPreview(null);
    });
  };

  // Copy/Cut one-or-many onto the clipboard (new multi-item shape).
  const copyNodes = (nodes, mode) => {
    setCtx(null);
    if (!nodes || !nodes.length) return;
    dispatch(
      setClipboard({
        mode,
        nodes: nodes.map((n) => ({ id: n.id, scope: n.scope, name: n.name, type: n.type })),
      })
    );
  };

  // Paste every clipboard entry into the target folder (current folder by default).
  const pasteInto = (targetParentId) => {
    const nodes = clipboardNodes(clipboard);
    if (!nodes.length || !scope) return;
    setCtx(null);
    run(async () => {
      for (const n of nodes) {
        await pasteNode(
          { id: n.id, mode: clipboard.mode, scope: n.scope, name: n.name, type: n.type },
          scope,
          targetParentId
        );
      }
      if (clipboard.mode === "cut") dispatch(clearClipboard());
    });
  };
  const paste = () => pasteInto(current.id);

  const selectAll = () => {
    setSelected(new Set(items.map((n) => n.id)));
    selectionAnchorRef.current = items.length ? 0 : null;
  };

  // Keyboard shortcuts (Windows-style). Active only inside a drive folder view.
  const onKeyDown = (e) => {
    if (!scope || preview || renamingId) return;
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea") return;
    const mod = e.ctrlKey || e.metaKey;
    const key = e.key.toLowerCase();
    if (mod && key === "a") {
      e.preventDefault();
      selectAll();
    } else if (mod && key === "c") {
      copyNodes(selectedNodes(), "copy");
    } else if (mod && key === "x") {
      copyNodes(selectedNodes(), "cut");
    } else if (mod && key === "v") {
      paste();
    } else if (e.key === "Delete") {
      removeNodes(selectedNodes());
    } else if (e.key === "Escape") {
      clearSelection();
    } else if (e.key === "F2") {
      const sel = selectedNodes();
      if (sel.length === 1) startRename(sel[0]);
    }
  };

  // ── rubber-band (marquee) drag selection on the empty folder area ────────────
  const onBodyMouseDown = (e) => {
    if (e.button !== 0) return; // left button only
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "input") return; // don't steal focus from an active rename box
    if (rootRef.current) rootRef.current.focus(); // enable keyboard shortcuts
    if (e.target.closest && e.target.closest(".webtop-files__item")) return; // item handles its own click
    if (!bodyRef.current) return;

    const additive = e.ctrlKey || e.metaKey;
    const preDrag = additive ? new Set(selected) : new Set();
    const startX = e.clientX;
    const startY = e.clientY;
    const br = bodyRef.current.getBoundingClientRect();
    const clampX = (x) => Math.max(br.left, Math.min(br.right, x));
    const clampY = (y) => Math.max(br.top, Math.min(br.bottom, y));
    let dragging = false;

    const onMove = (ev) => {
      if (!dragging) {
        if (Math.abs(ev.clientX - startX) < MARQUEE_THRESHOLD && Math.abs(ev.clientY - startY) < MARQUEE_THRESHOLD) {
          return;
        }
        dragging = true;
      }
      const x1 = clampX(startX);
      const y1 = clampY(startY);
      const x2 = clampX(ev.clientX);
      const y2 = clampY(ev.clientY);
      const rect = { left: Math.min(x1, x2), top: Math.min(y1, y2), right: Math.max(x1, x2), bottom: Math.max(y1, y2) };
      setMarquee({ left: rect.left, top: rect.top, width: rect.right - rect.left, height: rect.bottom - rect.top });

      const hit = new Set(preDrag);
      const els = bodyRef.current ? bodyRef.current.querySelectorAll(".webtop-files__item") : [];
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        const intersects = !(r.right < rect.left || r.left > rect.right || r.bottom < rect.top || r.top > rect.bottom);
        const id = el.getAttribute("data-id");
        if (intersects && id) hit.add(id);
      });
      setSelected(hit);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setMarquee(null);
      if (!dragging && !additive) clearSelection(); // plain click on empty space
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // ── context menus ───────────────────────────────────────────────────────────
  const onItemContextMenu = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    // Right-clicking an unselected item selects just it (Windows behaviour);
    // right-clicking inside an existing multi-selection keeps the selection.
    const targetNodes = selected.has(node.id) ? selectedNodes() : [node];
    if (!selected.has(node.id)) {
      setSelected(new Set([node.id]));
      selectionAnchorRef.current = items.findIndex((n) => n.id === node.id);
    }
    const multi = targetNodes.length > 1;
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        ...(multi ? [] : [{ label: "Open", onClick: () => openItem(node) }]),
        ...(multi ? [] : [{ label: "Rename", onClick: () => startRename(node) }]),
        { label: multi ? `Copy (${targetNodes.length})` : "Copy", onClick: () => copyNodes(targetNodes, "copy") },
        { label: multi ? `Cut (${targetNodes.length})` : "Cut", onClick: () => copyNodes(targetNodes, "cut") },
        ...(clipboardNodes(clipboard).length && node.type === "folder" && !multi
          ? [{ label: "Paste into folder", onClick: () => pasteInto(node.id) }]
          : []),
        ...(!multi && node.type === "file"
          ? [{ label: "Download", onClick: () => window.open(fileDownloadUrl(node), "_blank", "noopener") }]
          : []),
        { separator: true },
        {
          label: multi ? `Delete (${targetNodes.length})` : "Delete",
          danger: true,
          onClick: () => removeNodes(targetNodes),
        },
      ],
    });
  };

  // Right-click on the empty area of a folder → Windows-style background menu.
  const onBackgroundContextMenu = (e) => {
    // Let an item's own handler win (it stops propagation); only act on blank space.
    if (e.target.closest && e.target.closest(".webtop-files__item")) return;
    e.preventDefault();
    if (!scope) return; // "This PC" drive grid has no paste/new-folder context
    clearSelection();
    setCtx({
      x: e.clientX,
      y: e.clientY,
      items: [
        ...(clipboardNodes(clipboard).length ? [{ label: "Paste", onClick: paste }] : []),
        { label: "New folder", onClick: newFolder },
        { label: "New spreadsheet", onClick: newSheet },
        { label: "Upload files", onClick: () => fileInputRef.current && fileInputRef.current.click() },
        ...(items.length ? [{ separator: true }, { label: "Select all", onClick: selectAll }] : []),
      ],
    });
  };

  const inDrive = !!scope;
  const selCount = selected.size;
  const clipCount = clipboardNodes(clipboard).length;
  const clipLabel = clipCount === 1 ? clipboardNodes(clipboard)[0]?.name : `${clipCount} items`;

  return (
    <div className="webtop-files" ref={rootRef} tabIndex={0} onKeyDown={onKeyDown}>
      <div className="webtop-files__toolbar">
        <button
          type="button"
          className="webtop-files__navbtn"
          onClick={back}
          disabled={!preview && path.length <= 1}
          aria-label="Back"
          title="Back"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="webtop-files__crumbs">
          {path.map((c, i) => (
            <span key={`${c.scope || "root"}-${c.id || "root"}-${i}`} className="webtop-files__crumb">
              {i > 0 && <span className="webtop-files__crumbsep">›</span>}
              <button type="button" className="webtop-files__crumbbtn" onClick={() => goToCrumb(i)}>
                {c.name}
              </button>
            </span>
          ))}
        </div>
        {inDrive && !preview && (
          <div className="webtop-files__actions">
            {selCount > 0 && (
              <>
                <span className="webtop-files__selinfo">{selCount} selected</span>
                <button type="button" className="webtop-files__actbtn" onClick={() => copyNodes(selectedNodes(), "copy")} disabled={busy} title="Copy">
                  <FontAwesomeIcon icon={faCopy} /> Copy
                </button>
                <button type="button" className="webtop-files__actbtn" onClick={() => copyNodes(selectedNodes(), "cut")} disabled={busy} title="Cut">
                  <FontAwesomeIcon icon={faScissors} /> Cut
                </button>
                <button type="button" className="webtop-files__actbtn webtop-files__actbtn--danger" onClick={() => removeNodes(selectedNodes())} disabled={busy} title="Delete">
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </>
            )}
            {clipCount > 0 && (
              <button type="button" className="webtop-files__actbtn" onClick={paste} disabled={busy} title={`Paste ${clipLabel}`}>
                <FontAwesomeIcon icon={faPaste} /> Paste
              </button>
            )}
            <button type="button" className="webtop-files__actbtn" onClick={newFolder} disabled={busy} title="New folder">
              <FontAwesomeIcon icon={faFolderPlus} /> Folder
            </button>
            <button type="button" className="webtop-files__actbtn" onClick={newSheet} disabled={busy} title="New spreadsheet">
              <FontAwesomeIcon icon={faTableCells} /> Sheet
            </button>
            <button
              type="button"
              className="webtop-files__actbtn"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={busy}
              title="Upload files"
            >
              <FontAwesomeIcon icon={faFileArrowUp} /> Upload
            </button>
            <input ref={fileInputRef} type="file" multiple hidden onChange={onPickFiles} />
          </div>
        )}
      </div>

      {error && <div className="webtop-files__error" role="alert">{error}</div>}

      {/* "This PC" — drives (the personal Desktop folder lives inside the
          Personal drive, not as a separate quick-access tile). */}
      {!inDrive && (
        <div className="webtop-files__body">
          <div className="webtop-files__section">Devices and drives</div>
          <div className="webtop-files__grid webtop-files__grid--drives">
            {DRIVES.map((d) => {
              const quota = QUOTAS[d.scope] || GB;
              const used = Math.min(storage[d.scope] || 0, quota);
              const pct = Math.min(100, Math.round((used / quota) * 100));
              const nearFull = pct >= 90;
              return (
                <div
                  key={d.scope}
                  className="webtop-files__drive"
                  role="button"
                  tabIndex={0}
                  onDoubleClick={() => openDrive(d)}
                  onClick={() => openDrive(d)}
                  title={`${d.name} (${d.sub})`}
                >
                  <span className="webtop-files__drive-icon">
                    <FontAwesomeIcon icon={d.icon} />
                  </span>
                  <div className="webtop-files__drive-info">
                    <div className="webtop-files__name webtop-files__drive-name">
                      {d.name} <span className="webtop-files__drive-sub">({d.sub})</span>
                    </div>
                    <div className="webtop-files__drive-bar">
                      <div
                        className={`webtop-files__drive-fill${nearFull ? " webtop-files__drive-fill--full" : ""}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="webtop-files__drive-free">
                      {fmtGB(quota - used)} free of {fmtGB(quota)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Inside a drive — folder contents (incl. the personal Desktop folder). */}
      {inDrive && !preview && (
        <div
          className="webtop-files__body"
          ref={bodyRef}
          onMouseDown={onBodyMouseDown}
          onContextMenu={onBackgroundContextMenu}
        >
          {loading ? (
            <div className="webtop-files__empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="webtop-files__empty">
              This folder is empty. Create a folder or upload files.
            </div>
          ) : (
            <div className="webtop-files__grid">
              {items.map((node, index) => (
                <div
                  key={node.id}
                  data-id={node.id}
                  className={`webtop-files__item${selected.has(node.id) ? " webtop-files__item--selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handleItemClick(e, node, index)}
                  onContextMenu={(e) => onItemContextMenu(e, node)}
                  title={node.name}
                >
                  <span className={`webtop-files__glyph webtop-files__glyph--${node.type}`}>
                    {node.type === "file" && isImage(node) ? (
                      <img src={fileDownloadUrl(node, "inline")} alt="" className="webtop-files__thumb" />
                    ) : (
                      <FontAwesomeIcon icon={glyphForNode(node)} />
                    )}
                  </span>
                  {renamingId === node.id ? (
                    <input
                      className="webtop-files__rename"
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => commitRename(node.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitRename(node.id);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="webtop-files__name">{node.name}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* File preview (image or text) */}
      {preview && (
        <div className="webtop-files__preview">
          <div className="webtop-files__editorbar">
            <button type="button" className="webtop-files__navbtn" onClick={back} title="Back">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <span className="webtop-files__editorname">{preview.node.name}</span>
            <a className="webtop-files__save" href={fileDownloadUrl(preview.node)} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faDownload} /> Download
            </a>
          </div>
          {/* Only images preview here; text files open in Notepad (see openItem). */}
          <div className="webtop-files__previewbody">
            <img src={fileDownloadUrl(preview.node, "inline")} alt={preview.node.name} />
          </div>
        </div>
      )}

      {marquee && (
        <div
          className="webtop-files__marquee"
          style={{ left: marquee.left, top: marquee.top, width: marquee.width, height: marquee.height }}
        />
      )}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctx.items} onClose={() => setCtx(null)} />}
    </div>
  );
};

export default FileExplorerApp;
